/** @jsx jsx */

/** @jsxFrag React.Fragment */

/**
 * A static module that shows the career journey.
 *
 * Design: https://www.figma.com/file/C8v5uehGfADyaapI7dGiNZ/Q1-2023---Discovery-MVP?node-id=3542-108454&t=e50YD2owuzgVvD4M-0
 */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { BriefcaseIcon, CertificateIcon, SettingsIcon, UserIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/career-academy';

function EnterpriseCareerJourneyCard() {
  const styles = {
    journeyCard: css`
      display: flex;

      /* on desktop, icon and text are vertically arranged */
      flex-direction: column;
      justify-content: center;
      align-items: center;

      /* show the line crossing the icons */
      position: relative;

      &::before {
        content: '';
        border-bottom: 1px solid var(--cds-color-grey-50);
        display: block;
        width: 100%;
        height: 1px;
        position: absolute;
        top: 48px; /* = grid spacing (48px) / 2 + icon width (48px) / 2 */
        transform: translateX(50%); /* move the line to the center of the icon */
        z-index: 0; /* make sure the line is behind the icon */
      }

      /* don't show line for the last icon */
      &:last-child {
        &::before {
          display: none;
        }
      }
      ${breakpoints.down('sm')} {
        width: 100%;

        /* horizontally center the card in the container */
        margin-left: auto;
        margin-right: auto;

        /* on mobile, icon and text are horizontally arranged */
        flex-direction: row;
        justify-content: flex-start; /* left align inside the card */
        align-items: flex-start; /* top align inside the card */
        max-width: 500px;

        /* switch the line to vertical */
        &::before {
          content: '';
          border-left: 1px solid var(--cds-color-grey-50);
          width: 1px;
          height: 100%;
          position: absolute;
          left: 48px; /* = grid spacing (48px) / 2 + icon width (48px) / 2 */
        }
      }
    `,
    journeyIcon: css`
      background: var(--cds-color-white-0);
      border: 1px solid var(--cds-color-grey-50);
      width: 48px;
      height: 48px;
      min-width: 48px;
      border-radius: 50%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      margin-bottom: var(--cds-spacing-150);
      z-index: 1;
    `,
    journeyContent: css`
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      ${breakpoints.down('sm')} {
        align-items: flex-start;
        text-align: left;
        margin-left: var(--cds-spacing-150);
      }
    `,
  };

  return (
    <>
      <Grid item xs={12} sm={12} md={3} css={styles.journeyCard}>
        <div css={styles.journeyIcon}>
          <CertificateIcon size="medium" />
        </div>
        <div css={styles.journeyContent}>
          <Typography2 component="h3" variant="subtitleLarge">
            {_t('Develop')}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {_t('Train for in-demand careers with help from top industry partners. ')}
          </Typography2>
        </div>
      </Grid>
      <Grid item xs={12} sm={12} md={3} css={styles.journeyCard}>
        <div css={styles.journeyIcon}>
          <SettingsIcon size="medium" />
        </div>
        <div css={styles.journeyContent}>
          <Typography2 component="h3" variant="subtitleLarge">
            {_t('Practice')}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {_t('Get hands-on experience with the same tools you’ll use on the job.')}
          </Typography2>
        </div>
      </Grid>
      <Grid item xs={12} sm={12} md={3} css={styles.journeyCard}>
        <div css={styles.journeyIcon}>
          <BriefcaseIcon size="medium" />
        </div>
        <div css={styles.journeyContent}>
          <Typography2 component="h3" variant="subtitleLarge">
            {_t('Demonstrate')}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {_t('Prove you’ve got the skills by completing real-world projects.')}
          </Typography2>
        </div>
      </Grid>
      <Grid item xs={12} sm={12} md={3} css={styles.journeyCard}>
        <div css={styles.journeyIcon}>
          <UserIcon size="medium" />
        </div>
        <div css={styles.journeyContent}>
          <Typography2 component="h3" variant="subtitleLarge">
            {_t('Showcase')}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {_t('Show employers you’re job ready  with a shareable profile.')}
          </Typography2>
        </div>
      </Grid>
    </>
  );
}

export default EnterpriseCareerJourneyCard;
